import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import leadService, { LEAD_STATUS_GROUPS } from '../../services/leadService'
import { useAuthStore } from '../../store/authStore'
import {
  HiOutlineClipboardList, HiOutlinePhone, HiOutlineUserGroup, HiOutlineCheckCircle,
  HiOutlineXCircle, HiOutlineArrowRight, HiOutlineClock, HiOutlineExclamationCircle,
  HiOutlineBriefcase, HiOutlineTrendingUp, HiOutlineCalendar, HiOutlineUsers,
  HiOutlineLocationMarker, HiOutlineSpeakerphone, HiOutlineInboxIn,
} from 'react-icons/hi'

const GROUP_META = {
  untouched: { label: 'Chưa tiếp cận', badge: 'badge-neutral', bar: 'bg-secondary-400' },
  processing: { label: 'Đang xử lý', badge: 'badge-warning', bar: 'bg-amber-500' },
  positive: { label: 'Tích cực', badge: 'badge-success', bar: 'bg-emerald-500' },
  rejected: { label: 'Từ chối', badge: 'badge-danger', bar: 'bg-red-500' },
}

const fmtNum = (v) => {
  if (typeof v !== 'number') return v ?? 0
  if (v >= 10000) return (v / 1000).toFixed(v >= 100000 ? 0 : 1).replace(/\.0$/, '') + 'K'
  return v.toLocaleString('vi-VN')
}

function Stat({ to, icon: Icon, value, label, tone = 'primary', hint }) {
  const tones = {
    primary: 'bg-primary-50 text-primary-600', red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600', emerald: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600', secondary: 'bg-secondary-100 text-secondary-600',
  }
  const inner = (
    <div className="card h-full p-3 sm:p-4 flex items-center gap-2.5 sm:gap-4 min-w-0">
      <div className={`shrink-0 w-9 h-9 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <div className="min-w-0">
        <div className="text-lg sm:text-2xl font-bold text-secondary-900 truncate">{fmtNum(value)}</div>
        <div className="text-xs sm:text-sm text-secondary-500 truncate leading-tight">{label}</div>
        {hint && <div className="text-2xs text-secondary-400 mt-0.5 truncate">{hint}</div>}
      </div>
    </div>
  )
  return to ? <Link to={to} className="block hover:shadow-md transition rounded-xl">{inner}</Link> : inner
}

function BarRow({ label, value, max, tone = 'bg-primary-500', to }) {
  const pct = max ? Math.round((value * 100) / max) : 0
  const body = (
    <div className="py-1.5">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-secondary-700 truncate max-w-[65%]">{label}</span>
        <span className="font-semibold text-secondary-800">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary-100 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(pct, 3)}%` }} />
      </div>
    </div>
  )
  return to ? <Link to={to} className="block hover:opacity-80">{body}</Link> : body
}

// ─────────────────────────── CẤP QUẢN LÝ / ADMIN ───────────────────────────
function ManagerDashboard({ d }) {
  const maxSrc = Math.max(1, ...(d.by_source || []).map((x) => x.total))
  const maxArea = Math.max(1, ...(d.by_area || []).map((x) => x.total))
  const funnelMax = d.funnel?.[0]?.total || 1

  return (
    <>
      {/* KPI hành động */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat to="/admin/leads?range=today" icon={HiOutlineCalendar} value={d.today} label="Hồ sơ hôm nay" tone="primary" />
        <Stat to="/admin/leads?assigned_to=unassigned" icon={HiOutlineInboxIn} value={d.unassigned} label="Chưa phân công" tone="red" hint="cần chia ngay" />
        <Stat icon={HiOutlineExclamationCircle} value={d.overdue} label="Quá hẹn gọi lại" tone="amber" hint="đang trễ SLA" />
        <Stat icon={HiOutlineBriefcase} value={d.hired} label="Đã nhận việc" tone="emerald" />
        <Stat icon={HiOutlineTrendingUp} value={`${d.conversion}%`} label="Tỉ lệ chuyển đổi" tone="violet" hint="nhận việc / tổng" />
      </div>

      {/* Tổng / tuần / tháng — compact (số to, không icon) để không tràn trên mobile */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-8">
        {[['/admin/leads', d.total, 'Tổng hồ sơ'], ['/admin/leads?range=7d', d.week, '7 ngày qua'], ['/admin/leads?range=month', d.month, 'Tháng này']].map(([to, v, label]) => (
          <Link key={label} to={to} className="card p-3 text-center hover:shadow-md transition">
            <div className="text-xl sm:text-2xl font-bold text-secondary-900 truncate">{fmtNum(v)}</div>
            <div className="text-2xs sm:text-sm text-secondary-500 truncate">{label}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Phễu chuyển đổi */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Phễu chuyển đổi</h2>
          <div className="space-y-2.5">
            {d.funnel?.map((f, i) => (
              <div key={f.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-secondary-700">{f.label}</span>
                  <span className="text-secondary-500"><b className="text-secondary-800">{f.total}</b> · {f.pct}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-secondary-100 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-600"
                       style={{ width: `${Math.max((f.total * 100) / funnelMax, 2)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hiệu suất nhân viên */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">Hiệu suất nhân viên</h2>
            <Link to="/admin/report" className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">Báo cáo <HiOutlineArrowRight className="w-4 h-4" /></Link>
          </div>
          {d.by_handler?.length ? (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-2xs uppercase text-secondary-400 text-left">
                    <th className="px-2 py-1.5">Nhân viên</th>
                    <th className="px-2 py-1.5 text-center">Tổng</th>
                    <th className="px-2 py-1.5 text-center">Đang XL</th>
                    <th className="px-2 py-1.5 text-center">Tích cực</th>
                    <th className="px-2 py-1.5 text-center">Nhận việc</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-50">
                  {d.by_handler.slice(0, 8).map((h) => (
                    <tr key={h.user_id} className="hover:bg-secondary-50/60">
                      <td className="px-2 py-2 font-medium text-secondary-800 truncate max-w-[140px]">
                        <Link to={`/admin/leads?assigned_to=${h.user_id}`} className="hover:text-primary-600">{h.name}</Link>
                      </td>
                      <td className="px-2 py-2 text-center font-semibold">{h.total}</td>
                      <td className="px-2 py-2 text-center text-amber-600">{h.active}</td>
                      <td className="px-2 py-2 text-center text-emerald-600">{h.positive}</td>
                      <td className="px-2 py-2 text-center font-bold text-primary-600">{h.hired}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="text-sm text-secondary-400 py-6 text-center">Chưa có dữ liệu phân công.</div>}
        </div>

        {/* Nguồn lead */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2"><HiOutlineSpeakerphone className="w-5 h-5 text-secondary-400" /> Nguồn hồ sơ</h2>
          {d.by_source?.length ? d.by_source.map((s) => (
            <BarRow key={s.source} label={s.source} value={s.total} max={maxSrc} tone="bg-primary-500"
                    to={`/admin/leads?source=${encodeURIComponent(s.source)}`} />
          )) : <div className="text-sm text-secondary-400 py-4">Chưa có dữ liệu.</div>}
        </div>

        {/* Khu vực */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2"><HiOutlineLocationMarker className="w-5 h-5 text-secondary-400" /> Khu vực</h2>
          {d.by_area?.length ? d.by_area.map((a) => (
            <BarRow key={a.area} label={a.area} value={a.total} max={maxArea} tone="bg-emerald-500"
                    to={`/admin/leads?region=${encodeURIComponent(a.area)}`} />
          )) : <div className="text-sm text-secondary-400 py-4">Chưa có dữ liệu khu vực xác định.</div>}
        </div>
      </div>
    </>
  )
}

// ─────────────────────────── CẤP NHÂN VIÊN ───────────────────────────
function StaffDashboard({ d }) {
  return (
    <>
      {/* Việc hôm nay */}
      <div className="mb-2 text-sm font-semibold text-secondary-500 uppercase tracking-wide">🎯 Việc cần làm</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat to="/admin/leads" icon={HiOutlinePhone} value={d.need_action} label="Hồ sơ cần xử lý" tone="primary" hint="chưa tiếp cận / đang xử lý" />
        <Stat icon={HiOutlineExclamationCircle} value={d.overdue} label="Quá hẹn gọi lại" tone="red" hint="cần gọi gấp" />
        <Stat icon={HiOutlineClock} value={d.due_today} label="Hẹn gọi hôm nay" tone="amber" />
        <Stat to="/admin/leads?status=interview_scheduled" icon={HiOutlineCalendar} value={d.interview} label="Đã hẹn phỏng vấn" tone="emerald" />
      </div>

      {/* Hồ sơ của tôi */}
      <div className="mb-2 text-sm font-semibold text-secondary-500 uppercase tracking-wide">📋 Hồ sơ của tôi</div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat to="/admin/leads" icon={HiOutlineClipboardList} value={d.total} label="Tổng được giao" tone="secondary" />
        {['untouched', 'processing', 'positive', 'rejected'].map((k) => (
          <Stat key={k} to={`/admin/leads?group=${k}`} icon={HiOutlineUserGroup} value={d.by_group?.[k]} label={GROUP_META[k].label}
                tone={k === 'positive' ? 'emerald' : k === 'processing' ? 'amber' : k === 'rejected' ? 'red' : 'secondary'} />
        ))}
      </div>

      {/* Kết quả cá nhân */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Kết quả của tôi</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-emerald-50 p-4">
              <div className="text-3xl font-bold text-emerald-600">{d.hired ?? 0}</div>
              <div className="text-sm text-secondary-500 mt-1">Ứng viên đã nhận việc</div>
            </div>
            <div className="rounded-xl bg-violet-50 p-4">
              <div className="text-3xl font-bold text-violet-600">{d.conversion ?? 0}%</div>
              <div className="text-sm text-secondary-500 mt-1">Tỉ lệ chuyển đổi</div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">Tiến độ xử lý</h2>
            <Link to="/admin/forms" className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">Tạo chiến dịch riêng <HiOutlineArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="space-y-2.5">
            {d.funnel?.slice(0, 6).map((f) => (
              <div key={f.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-secondary-700">{f.label}</span>
                  <span className="text-secondary-500"><b className="text-secondary-800">{f.total}</b> · {f.pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-secondary-100 overflow-hidden">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.max(f.pct, 2)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function AdminDashboard() {
  const { user } = useAuthStore()
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    leadService.overview()
      .then((res) => { if (res.status === 'success') setD(res.data) })
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <div className="mb-6">
        <h1 className="page-title">Xin chào, {user?.full_name} 👋</h1>
        <p className="page-subtitle">{d?.is_manager ? 'Tổng quan điều hành tuyển dụng' : 'Việc của bạn hôm nay'}</p>
      </div>

      {loading || !d ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : d.is_manager ? <ManagerDashboard d={d} /> : <StaffDashboard d={d} />}
    </div>
  )
}

export default AdminDashboard
